// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Plus, Upload, Building2, MapPin } from 'lucide-react'
import { PageHeader, PageHeaderActions, Breadcrumbs } from '@/components/layout'
import { Button, Card, SetupGuide, Skeleton, ConfirmDialog, EmptyState, Pagination, CardKebabMenu } from '@/components/ui'
import { MobileSortControl } from '@/components/ui/MobileSortControl'
import { useProperties, useDeleteProperty } from '@/hooks/useProperties'
import { useListSort } from '@/hooks/useListSort'
import { useAuth } from '@/hooks/useAuth'
import { useNotificationStore } from '@/stores/useNotificationStore'

export function PropertyListPage() {
  const { t } = useTranslation('properties')
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  // EP-97: default-rule page — updated_at desc, user-selectable server-side sort.
  const { sort, order, setSort, toggleOrder, sortParams } = useListSort({
    defaultSort: 'updated_at',
    defaultOrder: 'desc',
  })
  const handleSort = (key: string) => { setPage(1); setSort(key) }
  const handleToggleOrder = () => { setPage(1); toggleOrder() }
  const { data, isLoading, isFetching, error, refetch } = useProperties(page, sortParams)

  const sortOptions = [
    { value: 'updated_at', label: tc('sort.by_last_updated') },
    { value: 'name', label: tc('sort.by_name') },
    { value: 'unit_count', label: t('labels.units') },
    { value: 'created_at', label: tc('sort.by_newest') },
  ]
  const deleteProperty = useDeleteProperty()
  const [confirmDeleteProperty, setConfirmDeleteProperty] = useState<{ id: number; name: string } | null>(null)
  const { user } = useAuth()
  // T110-1: the AI re-importer is admin + owner only, matching the D2 gate
  // CustomerListPage uses for its own Import affordance.
  const isAdminOrOwner = user?.role === 'admin' || user?.role === 'owner'

  const handleDelete = async () => {
    if (!confirmDeleteProperty) return
    try {
      await deleteProperty.mutateAsync(confirmDeleteProperty.id)
      setConfirmDeleteProperty(null)
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: { message?: string } } } }
      const message = axiosErr?.response?.data?.error?.message || tc('errors.something_went_wrong')
      useNotificationStore.getState().addToast({ type: 'error', message })
      // Dialog stays open so the user can read the message and click Cancel to dismiss
    }
  }

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: t('title') }]} />

        <PageHeader
          title={t('title')}
          description={t('description')}
          isRefreshing={isFetching && !isLoading}
        />

        {/* EP-97: sort toolbar — the card grid has no clickable headers, so this is
            the primary sort affordance. One row: sort on the LEFT, the Add Property
            action on the RIGHT (owner request 2026-07-27). The sort control shows
            only when there are properties to sort; Add Property stays visible. */}
        {/* One row, bottom-aligned so the Add Property button lines up with the
            sort Select + order toggle. The sort block flexes/shrinks (min-w-0) and
            the order button is icon-only on mobile, so Add Property (shrink-0) fits
            beside it instead of wrapping. */}
        <div className="flex items-end gap-2">
          {/* Spacer holds the sort control (when there are rows) and pushes the
              actions to the right. On mobile the secondary "Import with AI" action
              collapses into the ตัวเลือก/Options sheet (collapseOverflow) so the
              row stays [Options][Add Property]; desktop keeps both inline. */}
          <div className="flex-1 min-w-0">
            {data?.data && data.data.length > 0 && (
              <MobileSortControl
                size="sm"
                options={sortOptions}
                value={sort}
                order={order}
                onChange={handleSort}
                onToggleOrder={handleToggleOrder}
              />
            )}
          </div>
          <PageHeaderActions
            primaryAction={{
              key: 'add-property',
              label: t('actions.add_property'),
              icon: Plus,
              onClick: () => navigate('/properties/new'),
            }}
            overflowActions={[
              isAdminOrOwner && {
                key: 'import-ai',
                label: t('actions.import_ai'),
                icon: Upload,
                onClick: () => navigate('/properties/import'),
              },
            ]}
            collapseOverflow
          />
        </div>

        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <Card key={i}>
                <Skeleton className="h-6 w-2/3 mb-2" />
                <Skeleton className="h-4 w-full mb-4" />
                <Skeleton className="h-4 w-1/2" />
              </Card>
            ))}
          </div>
        ) : error ? (
          <Card>
            <EmptyState
              title={tc('errors.something_went_wrong')}
              action={<Button onClick={() => refetch()}>{tc('actions.retry')}</Button>}
            />
          </Card>
        ) : data?.data.length === 0 ? (
          <Card>
            <SetupGuide
              icon={<Building2 className="w-16 h-16" />}
              title={t('setup_guide.title')}
              description={t('setup_guide.description')}
              steps={[
                { title: t('setup_guide.step_1_title'), description: t('setup_guide.step_1_description') },
                { title: t('setup_guide.step_2_title'), description: t('setup_guide.step_2_description') },
                { title: t('setup_guide.step_3_title'), description: t('setup_guide.step_3_description') },
                { title: t('setup_guide.step_4_title'), description: t('setup_guide.step_4_description') },
              ]}
              action={
                <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => navigate('/properties/new')}>
                  {t('actions.add_property')}
                </Button>
              }
            />
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {data?.data.map((property) => (
              <Card
                key={property.id}
                data-testid="property-card"
                className="hover:shadow-md transition-shadow cursor-pointer group"
                onClick={() => navigate(`/properties/${property.id}`)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Building2 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold group-hover:text-primary transition-colors">
                        {property.name}
                      </h3>
                      {property.address && (
                        <p className="text-sm text-base-content/60 flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />
                          {property.address}
                        </p>
                      )}
                    </div>
                  </div>
                  <CardKebabMenu
                    items={[
                      {
                        label: tc('actions.delete'),
                        onClick: () => setConfirmDeleteProperty({ id: property.id, name: property.name }),
                        variant: 'error',
                      },
                    ]}
                  />
                </div>

                <div className="mt-4 pt-4 border-t border-base-200">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-base-content/60">{t('labels.room_types')}</span>
                    <span className="font-medium">{property.room_types?.length ?? 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-base-content/60">{t('labels.billing_day')}</span>
                    <span className="font-medium">{property.billing_day ?? 1}</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {data?.meta && data.meta.total_count > 0 && (
          <div className="flex justify-center">
            <Pagination
              currentPage={data.meta.current_page}
              totalPages={data.meta.total_pages}
              totalItems={data.meta.total_count}
              pageSize={data.meta.per_page}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmDeleteProperty !== null}
        onClose={() => setConfirmDeleteProperty(null)}
        onConfirm={handleDelete}
        title={t('messages.delete_title')}
        message={t('messages.delete_confirm', { name: confirmDeleteProperty?.name || '' })}
        confirmLabel={tc('actions.delete')}
        cancelLabel={tc('actions.cancel')}
        variant="error"
        loading={deleteProperty.isPending}
      />
    </>
  )
}
